import React, { useEffect, useRef, useState } from 'react';
import { API_URL } from './api';
import { getOfflineAccountScope, isOfflineScopeCurrent } from './scopedOfflineQueue';
import './StripeConnectSettings.css';

const statuses = { observed: 'Observé dans Stripe', applied: 'Rapproché dans la caisse', review_required: 'Revue manuelle requise' };
const coverageLabels = {
  never_checked: 'Aucune vérification Stripe enregistrée.',
  verified: 'Sélection vérifiée auprès de Stripe.',
  partial: 'Vérification partielle : certaines commandes restent à contrôler.',
  unavailable: 'Vérification Stripe indisponible. Les observations peuvent être anciennes.',
};
const reasons = {
  PARTIAL_REFUND_TAX_ALLOCATION_REQUIRED: 'Remboursement partiel : répartition de TVA à vérifier.',
  CAPTURE_TAX_PROOF_UNAVAILABLE: 'Justificatif fiscal du paiement à vérifier.',
  REFUND_STATUS_REVERSED_REQUIRES_REVIEW: 'Le statut du remboursement a changé : contrôler le rapprochement.',
  DISPUTE_REQUIRES_REVIEW: 'Litige Stripe à examiner avant régularisation.',
  CAPTURE_ON_CANCELLED_ORDER: 'Paiement constaté sur une commande annulée.',
  CAPTURE_TAX_LINES_UNAVAILABLE: 'Lignes fiscales de la commande indisponibles.',
  LOYALTY_RECONCILIATION_REQUIRED: 'Fidélité à vérifier : aucun ajustement automatique des points.',
  LOYALTY_POTENTIAL_REVIEW_REQUIRED: 'Fidélité potentielle à vérifier : aucun ajustement automatique des points.',
  REFUND_FAILED_REQUIRES_REVIEW: 'Remboursement échoué : vérifier le retour des fonds et la suite à donner.',
  REFUND_ACTION_REQUIRED: 'Stripe demande une action pour terminer le remboursement.',
};
const integer = value => Number.isSafeInteger(value) && value >= 0;
const timestamp = value => typeof value === 'string' && Number.isFinite(Date.parse(value));
const money = cents => (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const date = value => new Date(value).toLocaleString('fr-FR');
const unavailable = 'Le suivi après-vente est indisponible. Réessayez ultérieurement.';
const changedAccount = 'Le compte a changé. Rouvrez les paiements de l’établissement.';

function validSummary(data, accountId) {
  return data?.accountId === accountId && data.bounded === true && typeof data.reviewRequired === 'boolean'
    && (data.checkedAt === null || timestamp(data.checkedAt))
    && ['succeededCents', 'pendingCents', 'reviewRequiredCount'].every(key => integer(data.refunds?.[key]))
    && ['openCount', 'lostCount'].every(key => integer(data.disputes?.[key]))
    && data.coverage?.bounded === true && Object.hasOwn(coverageLabels, data.coverage.status)
    && ['checkedAt', 'lastSuccessAt'].every(key => data.coverage[key] === null || timestamp(data.coverage[key]))
    && ['checkedOrders', 'failedOrders', 'staleOrders'].every(key => integer(data.coverage[key]))
    && Array.isArray(data.observations) && data.observations.length <= 50
    && data.observations.every(row => Number.isSafeInteger(row.orderId) && row.orderId > 0
      && row.kind === 'reconciliation' && row.currency === 'eur' && timestamp(row.observedAt)
      && Object.hasOwn(statuses, row.accountingStatus)
      && (row.reviewReasons === undefined || (Array.isArray(row.reviewReasons) && row.reviewReasons.length <= 20 && row.reviewReasons.every(reason => typeof reason === 'string' && reason.length <= 150)))
      && ['succeededCents', 'pendingCents', 'refundCount', 'disputeCount', 'openDisputeCount', 'lostDisputeCount'].every(key => integer(row[key])));
}

function ScopedAfterSales({ request, expectedAccountId }) {
  const [scope] = useState(getOfflineAccountScope);
  const requestRef = useRef(request);
  requestRef.current = request;
  const validTarget = Number.isSafeInteger(expectedAccountId) && expectedAccountId > 0 && Boolean(scope);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(validTarget);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let active = true;
    if (!validTarget) return undefined;
    setLoading(true); setData(null); setError('');
    (async () => {
      try {
        if (!isOfflineScopeCurrent(scope)) throw new Error(changedAccount);
        const response = await requestRef.current(`${API_URL}/api/admin/payments/stripe/after-sales?expectedAccountId=${expectedAccountId}`, { method: 'GET', cache: 'no-store' });
        if (!active) return;
        if (!isOfflineScopeCurrent(scope)) throw new Error(changedAccount);
        if (!response.ok) throw new Error([401, 403].includes(response.status)
          ? 'Ce suivi est réservé à l’administrateur de l’établissement.'
          : response.status === 409 ? changedAccount : unavailable);
        const payload = await response.json();
        if (!active) return;
        if (!isOfflineScopeCurrent(scope) || payload?.accountId !== expectedAccountId) throw new Error(changedAccount);
        if (!validSummary(payload, expectedAccountId)) throw new Error(unavailable);
        setData(payload);
      } catch (failure) {
        if (active) setError([changedAccount, 'Ce suivi est réservé à l’administrateur de l’établissement.'].includes(failure.message) ? failure.message : unavailable);
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [expectedAccountId, scope, validTarget, revision]);

  return <section className="admin-panel stripe-connect" aria-labelledby="stripe-after-sales-title">
    <div className="admin-panel-head"><span className="admin-panel-kicker">Suivi des encaissements</span><h2 id="stripe-after-sales-title">Remboursements et litiges Stripe</h2></div>
    <p>Suivi en lecture seule des paiements de vos clients, sur les bornes, caisses, PAD et commandes en ligne.</p>
    <p className="stripe-connect-note">Sélection limitée à 50 commandes parmi les 250 dernières observations enregistrées. Les montants ci-dessous concernent cette sélection ; ils ne constituent pas un relevé comptable complet. Actualiser relit les observations enregistrées, sans interroger Stripe en direct.</p>
    {!validTarget ? <p role="alert" className="stripe-connect-error">Établissement non identifié. Rouvrez ses réglages de paiement.</p> : null}
    {loading ? <p role="status">Chargement du suivi après-vente…</p> : null}
    {error ? <p role="alert" className="stripe-connect-error">{error}</p> : null}
    {data ? <>
      <div className="stripe-connect-status">
        <strong>{coverageLabels[data.coverage.status]}</strong>
        {data.reviewRequired ? <p>Des opérations nécessitent une revue manuelle.</p> : data.coverage.status === 'verified' ? <p>Aucune revue manuelle signalée dans cette sélection.</p> : null}
        <p>{data.coverage.checkedAt ? `Dernier contrôle serveur : ${date(data.coverage.checkedAt)}.` : 'Aucun contrôle fournisseur enregistré.'}</p>
        {data.coverage.lastSuccessAt ? <p>Dernier contrôle réussi dans la sélection : {date(data.coverage.lastSuccessAt)}.</p> : null}
        <p>{data.coverage.checkedOrders} commande(s) suivie(s) · {data.coverage.failedOrders} contrôle(s) non abouti(s) · {data.coverage.staleOrders} commande(s) sans contrôle réussi récent.</p>
        {data.coverage.status === 'verified' || data.observations.length ? <dl className="stripe-connect-details">
          <div><dt>Remboursements confirmés</dt><dd>{money(data.refunds.succeededCents)}</dd></div>
          <div><dt>Remboursements en attente</dt><dd>{money(data.refunds.pendingCents)}</dd></div>
          <div><dt>Commandes à vérifier</dt><dd>{data.refunds.reviewRequiredCount}</dd></div>
          <div><dt>Litiges ouverts</dt><dd>{data.disputes.openCount}</dd></div>
          <div><dt>Litiges perdus</dt><dd>{data.disputes.lostCount}</dd></div>
        </dl> : <p>Les montants ne sont pas établis : ce suivi incomplet ne permet pas de conclure à l’absence de remboursement ou de litige.</p>}
      </div>
      {data.observations.length ? <div className="stripe-connect-table-wrap" role="region" aria-label="Observations après-vente Stripe" tabIndex={0}>
        <table className="stripe-connect-table"><thead><tr><th>Commande</th><th>Remboursements</th><th>Litiges</th><th>Rapprochement</th><th>Observation</th></tr></thead><tbody>
          {data.observations.map(row => <tr key={row.orderId}>
            <th scope="row">N° {row.orderId}</th>
            <td>Confirmés : {money(row.succeededCents)}<br />En attente : {money(row.pendingCents)}</td>
            <td>{row.openDisputeCount} ouvert(s)<br />{row.lostDisputeCount} perdu(s)</td>
            <td><strong>{statuses[row.accountingStatus]}</strong>{[...new Set(row.reviewReasons?.length ? row.reviewReasons : row.accountingStatus === 'review_required' ? [row.reviewReason] : [])].map((reason, index) => <p key={index}>{Object.hasOwn(reasons, reason) ? reasons[reason] : 'Vérifiez cette opération dans Stripe et dans la caisse.'}</p>)}</td>
            <td>{date(row.observedAt)}</td>
          </tr>)}
        </tbody></table>
      </div> : <p>Aucune observation après-vente enregistrée dans cette sélection.</p>}
    </> : null}
    <p>Les remboursements se réalisent dans Stripe. Les remboursements partiels, litiges et écarts demandent une vérification manuelle avant régularisation comptable.</p>
    <button type="button" className="admin-button admin-button-secondary" disabled={loading || !validTarget} onClick={() => setRevision(current => current + 1)}>Actualiser les observations</button>
  </section>;
}

export default function StripeAfterSalesSettings({ scopeKey, expectedAccountId, ...props }) {
  return <ScopedAfterSales key={`${scopeKey}:${expectedAccountId}`} expectedAccountId={expectedAccountId} {...props} />;
}
